import { useEffect, useState, type FormEvent } from 'react';

import { useCreditMutations } from '../../../hooks/useApi';
import type {
    PricingConfigPayload,
    PricingConfigSummary,
    PricingConfigUnitType,
} from '../../../services/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select } from '../../ui/select';
import { PricingConfigFields } from './PricingConfigFields';
import { PricingConfigSelectOptionLabel } from './PricingConfigSelectOptionLabel';
import {
    createDefaultPricingConfigFormValues,
    parsePricingConfigPayload,
    pricingSummaryToFormValues,
    type PricingConfigFormValues,
} from './pricingConfigForm';
import {
    Actions,
    AUDIT_REASON_FIELD_HINT,
    DialogFrame,
    Field,
    FormErrorAlert,
} from './creditDialogPrimitives';
import { getMutationErrorMessage } from './creditDialogHelpers';

export function AddPricingConfigDialog({
    open,
    onClose,
    onCreated,
    initialUnitType,
    pricingConfigs = [],
}: {
    open: boolean;
    onClose: () => void;
    onCreated?: (config: PricingConfigSummary) => void;
    initialUnitType?: PricingConfigUnitType;
    pricingConfigs?: PricingConfigSummary[];
}) {
    const { createPricingConfig } = useCreditMutations();
    const [values, setValues] = useState<PricingConfigFormValues>(
        createDefaultPricingConfigFormValues(),
    );
    const [templateId, setTemplateId] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setValues(createDefaultPricingConfigFormValues(initialUnitType ?? 'credit'));
        setTemplateId('');
        setError(null);
    }, [open, initialUnitType]);

    const handleTemplateChange = (nextTemplateId: string) => {
        setTemplateId(nextTemplateId);
        const template = pricingConfigs.find((config) => config.id === nextTemplateId);
        if (!template) return;
        setValues(pricingSummaryToFormValues(template));
        setError(null);
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        let config: PricingConfigPayload;
        try {
            config = parsePricingConfigPayload(values);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Pricing configuration is invalid.');
            return;
        }

        try {
            const result = await createPricingConfig.mutateAsync({
                name: values.name.trim(),
                description: values.description.trim() || null,
                config,
            });
            onCreated?.(result.pricingConfig);
            onClose();
        } catch (err) {
            setError(getMutationErrorMessage(err, 'Could not create pricing configuration.'));
        }
    };

    return (
        <DialogFrame
            open={open}
            onClose={onClose}
            title="+ Add Pricing Configuration"
            description="Create a pricing configuration with separate Pro and Pulse pricing sections."
            panelClassName="max-w-4xl"
        >
            <form className="space-y-4" onSubmit={handleSubmit}>
                {error ? <FormErrorAlert message={error} /> : null}
                {pricingConfigs.length > 0 ? (
                    <Field
                        label="Start from existing pricing configuration"
                        hint="Optional. Selecting a template prefills the form, then you can edit every field."
                    >
                        <Select
                            ariaLabel="Pricing configuration template"
                            value={templateId}
                            placeholder="Start blank"
                            options={[
                                { value: '', label: 'Start blank' },
                                ...pricingConfigs.map((config) => ({
                                    value: config.id,
                                    label: <PricingConfigSelectOptionLabel config={config} />,
                                })),
                            ]}
                            onValueChange={handleTemplateChange}
                        />
                    </Field>
                ) : null}
                <PricingConfigFields values={values} onChange={setValues} />
                <Actions onCancel={onClose}>
                    <Button type="submit" disabled={createPricingConfig.isPending}>
                        {createPricingConfig.isPending ? 'Creating...' : '+ Create Configuration'}
                    </Button>
                </Actions>
            </form>
        </DialogFrame>
    );
}

export function EditPricingConfigDialog({
    open,
    pricingConfig,
    onClose,
    onCreated,
}: {
    open: boolean;
    pricingConfig: PricingConfigSummary | null;
    onClose: () => void;
    onCreated?: (config: PricingConfigSummary) => void;
}) {
    const { createPricingConfigVersion } = useCreditMutations();
    const [values, setValues] = useState<PricingConfigFormValues>(
        createDefaultPricingConfigFormValues(),
    );
    const [reason, setReason] = useState('Admin pricing configuration edit');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open || !pricingConfig) return;
        setValues(pricingSummaryToFormValues(pricingConfig));
        setReason('Admin pricing configuration edit');
        setError(null);
    }, [open, pricingConfig]);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (!pricingConfig) return;
        if (!reason.trim()) {
            setError('Reason is required.');
            return;
        }

        let config: PricingConfigPayload;
        try {
            config = parsePricingConfigPayload(values);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Pricing configuration is invalid.');
            return;
        }

        try {
            const result = await createPricingConfigVersion.mutateAsync({
                id: pricingConfig.id,
                request: {
                    name: values.name.trim(),
                    description: values.description.trim() || null,
                    config,
                    reason: reason.trim(),
                },
            });
            onCreated?.(result.pricingConfig);
            onClose();
        } catch (err) {
            setError(
                getMutationErrorMessage(err, 'Could not create pricing configuration version.'),
            );
        }
    };

    return (
        <DialogFrame
            open={open}
            onClose={onClose}
            title="Edit Pricing Configuration"
            description={
                pricingConfig
                    ? `Saving creates version ${pricingConfig.version + 1} as a new row. Existing SOWs that use version ${pricingConfig.version} will keep using it.`
                    : undefined
            }
            panelClassName="max-w-4xl"
        >
            <form className="space-y-4" onSubmit={handleSubmit}>
                {error ? <FormErrorAlert message={error} /> : null}
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                    This edit does not mutate the selected version. It creates a new pricing
                    configuration version so existing SOWs remain unchanged.
                </div>
                <PricingConfigFields values={values} onChange={setValues} />
                <Field label="Reason" required hint={AUDIT_REASON_FIELD_HINT}>
                    <Input value={reason} onChange={(event) => setReason(event.target.value)} />
                </Field>
                <Actions onCancel={onClose}>
                    <Button type="submit" disabled={createPricingConfigVersion.isPending}>
                        {createPricingConfigVersion.isPending ? 'Saving...' : 'Create New Version'}
                    </Button>
                </Actions>
            </form>
        </DialogFrame>
    );
}
